import React, { useEffect, useState } from "react";
import api, { rupees, formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHead, Loading, Pill, BrandDot, Empty } from "@/components/common";
import { toast } from "sonner";
import { Plus, X, Download, ImageDown } from "lucide-react";

export default function QRCodes() {
  const { selectedBrand, brands } = useApp();
  const [batches, setBatches] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [skus, setSkus] = useState([]);
  const [form, setForm] = useState({ brand_id: "", sku_id: "", quantity: 100, base_reward_points: 20 });

  const load = () => {
    const b = selectedBrand === "ALL" ? "" : `?brand=${selectedBrand}`;
    api.get(`/qr/batches${b}`).then((r) => setBatches(r.data));
  };
  useEffect(() => { load(); }, [selectedBrand]);

  const openNew = () => {
    const bid = selectedBrand === "ALL" ? brands[0]?.id : selectedBrand;
    setForm({ brand_id: bid, sku_id: "", quantity: 100, base_reward_points: 20 });
    api.get(`/skus?brand=${bid}&page_size=200`).then((r) => setSkus(r.data.items));
    setShowNew(true);
  };

  const create = async () => {
    try {
      const { data } = await api.post("/qr/batches", form);
      toast.success(`Batch created · est. liability ${rupees(data.estimated_base_liability_paise)}`);
      setShowNew(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const activate = async (id) => { await api.post(`/qr/batches/${id}/activate`); toast.success("Batch activated"); load(); };

  const [exporting, setExporting] = useState(null);
  const exportBatch = async (b, kind = "csv") => {
    if (exporting) return;
    setExporting(`${b.id}:${kind}`);
    const isZip = kind === "png";
    try {
      const res = await api.get(`/qr/batches/${b.id}/${isZip ? "export-images" : "export"}`, { responseType: "blob" });
      const rows = parseInt(res.headers["x-row-count"] ?? "-1", 10);
      if (rows !== b.quantity) throw new Error(`Row count mismatch: expected ${b.quantity}, received ${rows}. File not saved.`);
      const url = URL.createObjectURL(new Blob([res.data], { type: isZip ? "application/zip" : "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = isZip ? `qr_batch_${b.id}_images.zip` : `qr_batch_${b.id}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${isZip ? "QR images ZIP" : "CSV"} downloaded · ${rows} of ${b.quantity} codes`);
    } catch (e) {
      let msg = e.message;
      if (e.response?.data instanceof Blob) {
        try { msg = formatApiError(JSON.parse(await e.response.data.text()).detail); } catch { /* keep msg */ }
      }
      toast.error(msg || "Export failed");
    } finally { setExporting(null); }
  };

  return (
    <div>
      <PageHead overline="QR Management" title="QR Codes" desc="Each QR maps to one physical unit with a frozen base reward version. Opening a link never consumes a code."
        actions={<button data-testid="new-qr-batch-btn" onClick={openNew} className="flex items-center gap-2 text-sm font-semibold text-white tnp-gradient px-3 py-2 rounded-lg"><Plus size={15} /> New Batch</button>} />

      {!batches ? <Loading /> : batches.length === 0 ? <Empty msg="No QR batches yet. Create one to begin." /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {batches.map((b) => (
            <div key={b.id} data-testid={`qr-batch-${b.id}`} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700"><BrandDot id={b.brand_id} />{b.sku_name}</div>
                <div className="flex items-center gap-2">
                  <button data-testid={`qr-export-${b.id}`} disabled={!!exporting} onClick={() => exportBatch(b)} title="Download printer-usable CSV (all codes)" className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-violet-600 disabled:opacity-50 transition-colors"><Download size={15} />{exporting === `${b.id}:csv` ? "…" : "CSV"}</button>
                  <button data-testid={`qr-export-images-${b.id}`} disabled={!!exporting} onClick={() => exportBatch(b, "png")} title="Download ZIP of QR images (one PNG per code + manifest)" className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-violet-600 disabled:opacity-50 transition-colors"><ImageDown size={15} />{exporting === `${b.id}:png` ? "…" : "PNG"}</button>
                  <Pill status={b.status}>{b.status}</Pill>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center my-4">
                <div><div className="text-lg font-bold font-head">{b.quantity}</div><div className="text-[10px] text-slate-400 uppercase">Units</div></div>
                <div><div className="text-lg font-bold font-head text-violet-600">{b.base_reward_points}</div><div className="text-[10px] text-slate-400 uppercase">Base pts</div></div>
                <div><div className="text-lg font-bold font-head text-emerald-600">{b.claimed}</div><div className="text-[10px] text-slate-400 uppercase">Claimed</div></div>
              </div>
              {b.demo && <div className="text-[11px] text-amber-600 mb-2">Sandbox demo batch</div>}
              {b.status === "draft" ? (
                <button data-testid={`qr-activate-${b.id}`} onClick={() => activate(b.id)} className="w-full text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 py-2 rounded-lg hover:bg-emerald-100">Activate batch</button>
              ) : (
                <div className="w-full text-xs text-slate-400 text-center py-2 bg-slate-50 rounded-lg">Reward version v{b.reward_version} · frozen</div>
              )}
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="w-full max-w-md bg-white rounded-xl p-6 fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-bold font-head">New QR Batch</h3><button onClick={() => setShowNew(false)}><X size={18} className="text-slate-400" /></button></div>
            <div className="space-y-3">
              <div><label className="text-xs font-semibold text-slate-600">Brand</label>
                <select data-testid="qr-brand-select" value={form.brand_id} onChange={(e) => { setForm({ ...form, brand_id: e.target.value, sku_id: "" }); api.get(`/skus?brand=${e.target.value}&page_size=200`).then((r) => setSkus(r.data.items)); }} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm mt-1">
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-semibold text-slate-600">SKU</label>
                <select data-testid="qr-sku-select" value={form.sku_id} onChange={(e) => setForm({ ...form, sku_id: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm mt-1">
                  <option value="">Select SKU</option>
                  {skus.map((s) => <option key={s.id} value={s.id}>{s.display_code} · {s.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-semibold text-slate-600">Quantity</label><input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value || 0) })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm mt-1" /></div>
                <div><label className="text-xs font-semibold text-slate-600">Base reward pts</label><input data-testid="qr-points-input" type="number" value={form.base_reward_points} onChange={(e) => setForm({ ...form, base_reward_points: parseInt(e.target.value || 0) })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm mt-1" /></div>
              </div>
              <div className="text-xs text-slate-400">Est. base liability: {rupees((form.quantity || 0) * (form.base_reward_points || 0) * 25)}</div>
              <button data-testid="qr-create-btn" onClick={create} className="w-full tnp-gradient text-white font-semibold py-2.5 rounded-lg">Create draft batch</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
